import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Clock, User } from "lucide-react";
import SEO from "@/components/SEO";
import { getBlogBySlug, blogPosts } from "@/data/blog";
import CTASection from "@/components/sections/CTASection";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const others = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "image": post.cover,
    "datePublished": post.publishedAt,
    "author": { "@type": "Organization", "name": "YAFORMIS" },
    "publisher": {
      "@type": "Organization",
      "name": "YAFORMIS",
      "logo": { "@type": "ImageObject", "url": "https://www.gme-assistance.fr/logo-yaformis.png" }
    }
  };

  return (
    <>
      <SEO
        title={post.seoTitle.replace(" | YAFORMIS", "").replace(" | YAFORMIS", "")}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        image={post.cover}
        type="article"
        schema={articleSchema}
      />

      <article>
        <section className="bg-brand-page py-12 md:pt-16 pb-10 border-b border-brand-line" data-testid="blogpost-hero">
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-ink" data-testid="blogpost-back">
              ← Tous les articles
            </Link>
            <div className="overline mt-6">{post.category}</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] text-brand-ink">
              {post.title}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.readingTime}</span>
              <span>· {new Date(post.publishedAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</span>
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="max-w-5xl mx-auto px-5 md:px-8">
            <img src={post.cover} alt={post.title} className="w-full aspect-[16/9] object-cover" data-testid="blogpost-cover" />
          </div>
        </section>

        <section className="bg-brand-page py-10 md:py-14" data-testid="blogpost-content">
          <div className="max-w-3xl mx-auto px-5 md:px-8 space-y-5 font-body text-[17px] md:text-lg leading-[1.75] text-slate-700">
            {post.content.map((block, i) => {
              if (block.type === "h2") return <h2 key={i} className="font-heading font-black text-2xl md:text-3xl tracking-tight text-brand-ink mt-10 mb-1 leading-tight">{block.text}</h2>;
              if (block.type === "h3") return <h3 key={i} className="font-heading font-bold text-xl tracking-tight text-brand-ink mt-7 mb-1">{block.text}</h3>;
              if (block.type === "ul")
                return (
                  <ul key={i} className="space-y-2.5 pl-0 list-none">
                    {block.items.map((it, j) => (
                      <li key={j} className="flex gap-3 items-start"><span className="text-brand-blue font-bold mt-1.5 shrink-0">→</span><span>{it}</span></li>
                    ))}
                  </ul>
                );
              return <p key={i}>{block.text}</p>;
            })}
          </div>
        </section>

        <section className="bg-brand-surface py-12 md:py-16" data-testid="blogpost-cta">
          <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
            <div className="overline">Vous voulez maîtriser l'IA ?</div>
            <h2 className="font-heading font-black tracking-tighter text-2xl md:text-4xl mt-3 leading-tight text-brand-ink">Passez à la pratique avec une formation YAFORMIS.</h2>
            <Link to="/formations" className="mt-6 btn-cta" data-testid="blogpost-cta-button">
              Voir les formations <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        <section className="bg-brand-page py-12 md:py-16 border-t border-brand-line" data-testid="blogpost-related">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="overline">À lire ensuite</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl mt-3 leading-tight text-brand-ink">Articles similaires</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blogpost-related-${p.slug}`}
                  className="group bg-white border border-brand-line hover:border-brand-ink transition-colors flex flex-col"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#0a1830]">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-xs font-semibold text-brand-blue uppercase tracking-wider">{p.category}</div>
                    <h3 className="font-heading font-bold text-lg mt-2 leading-snug text-brand-ink group-hover:text-brand-blue transition-colors">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </article>

      <CTASection />
    </>
  );
}
